<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import MessageApi from 'dashboard/api/inbox/message';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import Avatar from 'next/avatar/Avatar.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  messageId: { type: [Number, String], required: true },
});

const { t } = useI18n();

const isFetching = ref(true);
const hasError = ref(false);
const mentioned = ref([]);
const others = ref([]);
const messageCreatedAt = ref(0);
const trackingStartedAt = ref(0);

const isOlderThanTracking = computed(
  () =>
    !!messageCreatedAt.value && messageCreatedAt.value < trackingStartedAt.value
);

const hasNoReaders = computed(
  () => !others.value.length && !mentioned.value.some(person => person.read_at)
);

const readLabel = person =>
  t('CONVERSATION.READ_RECEIPTS.READ_AT', {
    time: messageTimestamp(person.read_at, 'MMM d, h:mm a'),
  });

onMounted(async () => {
  try {
    const { data } = await MessageApi.getReadReceipts(
      props.conversationId,
      props.messageId
    );
    mentioned.value = data.mentioned;
    others.value = data.others;
    messageCreatedAt.value = data.message_created_at;
    trackingStartedAt.value = data.tracking_started_at;
  } catch (error) {
    hasError.value = true;
  } finally {
    isFetching.value = false;
  }
});
</script>

<template>
  <div class="flex flex-col w-full p-8 pt-6">
    <h2 class="text-base font-medium text-n-slate-12">
      {{ $t('CONVERSATION.READ_RECEIPTS.TITLE') }}
    </h2>
    <p class="mt-1 mb-4 text-sm text-n-slate-11">
      {{ $t('CONVERSATION.READ_RECEIPTS.SUBTITLE') }}
    </p>

    <div v-if="isFetching" class="flex justify-center py-6">
      <Spinner />
    </div>
    <p v-else-if="hasError" class="py-4 text-sm text-n-ruby-11">
      {{ $t('CONVERSATION.READ_RECEIPTS.ERROR') }}
    </p>
    <template v-else>
      <p
        v-if="isOlderThanTracking && hasNoReaders"
        class="p-2 mb-4 text-sm rounded-md bg-n-solid-amber text-n-slate-12"
      >
        {{ $t('CONVERSATION.READ_RECEIPTS.BEFORE_TRACKING') }}
      </p>

      <div v-if="mentioned.length" class="flex flex-col gap-2 mb-4">
        <span class="text-xs font-medium uppercase text-n-slate-10">
          {{ $t('CONVERSATION.READ_RECEIPTS.MENTIONED') }}
        </span>
        <div
          v-for="person in mentioned"
          :key="person.id"
          class="flex items-center gap-2"
        >
          <Avatar :name="person.name" :src="person.thumbnail" :size="24" />
          <span class="text-sm text-n-slate-12">{{ person.name }}</span>
          <span
            v-if="person.read_at"
            class="ltr:ml-auto rtl:mr-auto text-xs text-n-teal-11"
          >
            {{ readLabel(person) }}
          </span>
          <span v-else class="ltr:ml-auto rtl:mr-auto text-xs text-n-slate-10">
            {{
              isOlderThanTracking
                ? $t('CONVERSATION.READ_RECEIPTS.UNKNOWN')
                : $t('CONVERSATION.READ_RECEIPTS.NOT_READ')
            }}
          </span>
        </div>
      </div>

      <div v-if="others.length" class="flex flex-col gap-2">
        <span class="text-xs font-medium uppercase text-n-slate-10">
          {{ $t('CONVERSATION.READ_RECEIPTS.OTHERS') }}
        </span>
        <div
          v-for="person in others"
          :key="person.id"
          class="flex items-center gap-2"
        >
          <Avatar :name="person.name" :src="person.thumbnail" :size="24" />
          <span class="text-sm text-n-slate-12">{{ person.name }}</span>
          <span class="ltr:ml-auto rtl:mr-auto text-xs text-n-teal-11">
            {{ readLabel(person) }}
          </span>
        </div>
      </div>

      <p
        v-if="hasNoReaders && !mentioned.length"
        class="py-2 text-sm text-n-slate-11"
      >
        {{ $t('CONVERSATION.READ_RECEIPTS.NOBODY') }}
      </p>
    </template>
  </div>
</template>
